import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { copyDevicesFromASpaceToAnotherSpace, IDevice } from "@multiconsult-group/multiplacer-core";
import { IAppThunk } from "../../../app/store";
import { selectCachedMcbimBuildingStoriesForCurrentProject } from "../../../DesignTab/designSelector";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
  removeNonPublischedDevicesFromAlgosInRedux,
  thunkToast,
} from "../../../DesignTab/designSlice";
import { selectHttpDevicesInRoom } from "../../../DesignTab/devices/deviceSlice";
import { withWorldZInCoordinate } from "../../../DesignTab/devices/deviceUtils";
import { selectActivePartialMcbimSpace } from "../../../McBim/mcbimSelectors";
import { selectHttpSingleRoomFull } from "../../../Room/httpRoomSelectors";
import {
  selectSelectedSimilarRoomWithDevicesAsMcbimSpace,
  selectSimilarRoomWithDevicesSourceDevices,
} from "./similarRoomWithDevicesViewerSelectors";

export const thunkCopySimilarRoomWithDevicesToCurrentRoom = (): IAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    const sourceSpace = selectSelectedSimilarRoomWithDevicesAsMcbimSpace(state);
    const targetSpace = selectActivePartialMcbimSpace(state);
    const targetRoom = selectHttpSingleRoomFull(state);
    const sourceDevices = selectSimilarRoomWithDevicesSourceDevices(state);
    const targetDevices = selectHttpDevicesInRoom(state);
    const nonPublishedDevices = state.design.nonPublischedDevicesFromAlgos;

    if (!sourceSpace || !targetSpace) {
      dispatch(
        thunkToast({
          text: "Kilde- eller målrom mangler.",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    if (sourceSpace.name === targetSpace.name) {
      return;
    }

    if (sourceDevices.length === 0) {
      dispatch(
        thunkToast({
          text: "Fant ingen komponenter å kopiere fra valgt rom.",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    if (targetDevices.length > 0) {
      dispatch(
        thunkToast({
          text: "Det finnes allerede komponenter i aktuelt rom.",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    const existingNonPublishedInTarget = nonPublishedDevices.filter(
      (device) => device.roomNumber === targetSpace.name
    );

    if (existingNonPublishedInTarget.length > 0) {
      dispatch(
        removeNonPublischedDevicesFromAlgosInRedux(
          existingNonPublishedInTarget.map((device) => String(device._id))
        )
      );
    }

    const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
      devicesToCopy: sourceDevices as IDevice[],
      spaceToCopyFrom: sourceSpace,
      spaceToCopyTo: targetSpace,
      roomToCopyTo: (targetRoom ?? undefined) as IRoomSummary | undefined,
    });

    if (copiedDevices.length === 0) {
      dispatch(
        thunkToast({
          text: `Ingen komponenter kunne kopieres fordi aktuelt rom ${targetSpace.name} skiller seg for mye fra kilderommet ${sourceSpace.name}.`,
          header: "Advarsel",
          isSuccess: false,
        })
      );
      return;
    }

    const activeFloorElevationM = state.design.floorAndJob?.elevation;
    const buildingStories = selectCachedMcbimBuildingStoriesForCurrentProject(state);
    const buildingStory = buildingStories.find(
      (story) => story.name === targetSpace.buildingStory?.name
    );
    const heightFromFloor = getHeightFromFirstCeilingGrid(targetSpace.ceilingGrids ?? []) ?? 2700;
    const floorElevationM = buildingStory?.elevation ?? activeFloorElevationM;

    const enrichedCopiedDevices = copiedDevices.map((device) => ({
      ...device,
      floor: targetSpace.buildingStory?.name ?? device.floor,
      roomName: targetSpace.longName ?? targetRoom?.roomName ?? device.roomName,
      bottomEdgeHeightAboveFloorMm: heightFromFloor,
      coordinate: withWorldZInCoordinate(device.coordinate, floorElevationM, heightFromFloor),
    }));

    dispatch(addNonPublischedDevicesFromAlgosInRedux(enrichedCopiedDevices));
    dispatch(
      thunkToast({
        text: `Kopierte ${enrichedCopiedDevices.length} komponenter til aktuelt rom.`,
        header: "Suksess",
        isSuccess: true,
      })
    );
  };
};
